import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { signup } from '../../fetchers/signup';
import { useAlert } from '../../context/AlertProvider';
import { getLangCountry } from '../../helpers/languages';
import BlueButtonBig from '../BlueButtonBig';
import FormErrorMessage from '../FormErrorMessage';
import FormInput from '../FormInput';
import Spinner from '../Spinner';
import Link from 'next/link';

const SignupPresentational = () => {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const alert = useAlert();
  const { t } = useTranslation();
  const { lang, country } = getLangCountry(router.query);

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors }
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      passwordConfirm: '',
      newsletter: false,
      privacy: false
    }
  });

  const password = watch('password');

  const onSignUp = async (): Promise<void> => {
    const { first_name, last_name, email, password, newsletter, privacy } =
      getValues();

    setLoading(true);

    const { error } = await signup({
      first_name,
      last_name,
      email,
      password,
      newsletter: !!newsletter,
      privacy,
      lang,
      country
    });

    setLoading(false);

    if (error) {
      alert.error(t(error.response?.data?.message || 'GENERIC_ERROR'));
      return;
    }

    setSuccess(true);
  };

  return (
    <div className="bg-grey-custom flex flex-col items-center justify-center py-16 px-4">
      <h1 className="font-sans text-5xl text-center mb-4 ">
        {t('CREATE_ACCOUNT')}
      </h1>
      <div className="m-auto rounded-lg bg-white p-8 w-full lg:w-1/2">
        <h2 className="font-sans text-3xl py-4 ">{t('NEW_ACCOUNT')}</h2>

        <div>
          {success ? (
            <p className="text-sm opacity-60 mb-4">
              {t('SIGNUP_CONFIRM_TEXT')}
            </p>
          ) : (
            <form onSubmit={handleSubmit(onSignUp)}>
              <div className="my-2">
                <FormInput
                  label={t('NAME')}
                  type="text"
                  id="first_name"
                  placeholder={t('NAME')}
                  invalid={Boolean(errors.first_name)}
                  {...register('first_name', {
                    required: t('REQUIRED_FIELD') as string
                  })}
                />

                <FormErrorMessage errors={errors} name="first_name" />
              </div>
              <div className="my-2">
                <FormInput
                  label={t('SURNAME')}
                  type="text"
                  id="last_name"
                  placeholder={t('SURNAME')}
                  invalid={Boolean(errors.last_name)}
                  {...register('last_name', {
                    required: t('REQUIRED_FIELD') as string
                  })}
                />

                <FormErrorMessage errors={errors} name="last_name" />
              </div>

              <div className="my-2">
                <FormInput
                  label="Email"
                  type="email"
                  id="email"
                  placeholder={t('MAIL_ADDRESS')}
                  invalid={Boolean(errors.email)}
                  {...register('email', {
                    required: t('REQUIRED_FIELD') as string,
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,10}$/i,
                      message: t('INVALID_EMAIL')
                    }
                  })}
                />

                <FormErrorMessage errors={errors} name="email" />
              </div>

              <div className="my-2">
                <FormInput
                  label="Password"
                  type="password"
                  id="password"
                  placeholder={t('PASSWORD_PLACEHOLDER')}
                  invalid={Boolean(errors.password)}
                  {...register('password', {
                    required: t('REQUIRED_FIELD') as string,
                    minLength: {
                      value: 6,
                      message: t('PASSWORD_MIN_LENGTH')
                    }
                  })}
                />

                <FormErrorMessage errors={errors} name="password" />
              </div>

              <div className="my-2">
                <FormInput
                  label="Password"
                  type="password"
                  id="passwordConfirm"
                  placeholder={t('CONFIRM_PASSWORD')}
                  invalid={Boolean(errors.passwordConfirm)}
                  {...register('passwordConfirm', {
                    required: t('REQUIRED_FIELD') as string,
                    validate: value =>
                      value === password || (t('PASSWORD_MISSMATCH') as string),
                    minLength: {
                      value: 6,
                      message: t('PASSWORD_MIN_LENGTH')
                    }
                  })}
                />

                <FormErrorMessage errors={errors} name="passwordConfirm" />
              </div>

              <div>
                <input
                  type="checkbox"
                  id="newsletter"
                  {...register('newsletter')}
                />
                <label htmlFor="newsletter" className="ml-4">
                  {t('FORM_PRIVACY_CONSENT')}
                </label>

                <FormErrorMessage errors={errors} name="newsletter" />
              </div>

              <div className="my-4 mb-8">
                <input
                  type="checkbox"
                  id="privacy"
                  {...register('privacy', {
                    required: t('REQUIRED_FIELD') as string
                  })}
                />
                <label htmlFor="privacy" className="ml-4">
                  {t('DATA_CONSENT')}
                  <Link href={`/${lang}_${country}/terms-and-conditions`}>
                    {t('CLICK_HERE')}
                  </Link>
                </label>

                <div>
                  <FormErrorMessage errors={errors} name="privacy" />
                </div>
              </div>

              {loading ? (
                <Spinner />
              ) : (
                <BlueButtonBig text={t('CREATE_ACCOUNT')} />
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupPresentational;
